import { useContext, useCallback, useEffect, useState } from "react";
import { TranslationService } from "../../../../services/TranslationService";
import CompanyService from "../../../../services/CompanyService";
import ConfigurationContext from "../../ConfigurationContext";
import ButtonPrimary from "../../../account/components/ButtonPrimary";
import * as yup from "yup";
import { TitleConfig } from "../components/TitleConfig";
import {
  ToggleEnableActionWrapper,
  convertBool
} from "./ToggleEnableActionWrapper";
import { NumberInput } from "../../../shared/components/Editors";

type UnlimitedSMSConfigurationType = {
  initialUnlimitedValue: "False" | "True";
  initialLongSMSValue?: number;
  fullSize?: boolean;
};

const DEFAULT_LONG_SMS = 500;
const MIN_LONG_SMS = 1;

export const UnlimitedSMSConfiguration = ({
  initialUnlimitedValue,
  initialLongSMSValue = DEFAULT_LONG_SMS,
  fullSize = false
}: UnlimitedSMSConfigurationType) => {
  const [saveData, setSaveData] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [longSMS, setLongSMS] = useState<number>(
    initialLongSMSValue || DEFAULT_LONG_SMS
  );
  const unlimitedChecked = convertBool[initialUnlimitedValue];
  const { refreshConfigValues } = useContext(ConfigurationContext);
  const { translate } = TranslationService;

  const formDataSchema = yup.object({
    longSMS: yup
      .number()
      .min(MIN_LONG_SMS, `El valor mínimo debe ser ${MIN_LONG_SMS}`)
      .required(`El valor mínimo debe ser ${MIN_LONG_SMS}`)
      .typeError(`El valor mínimo debe ser ${MIN_LONG_SMS}`)
  });

  const handleLongSMSChange = ({
    target: { value }
  }: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = parseInt(value) || DEFAULT_LONG_SMS;
    setLongSMS(numValue);
    setSaveData(true);
  };

  const saveValues = useCallback(() => {
    setSaveData(false);
    setSaving(true);
    CompanyService.setConfigCompany({ longSMS: longSMS }).finally(async () => {
      await refreshConfigValues();
      setSaving(false);
    });
  }, [longSMS, refreshConfigValues]);

  useEffect(() => {
    if (!unlimitedChecked) {
      formDataSchema
        .validate({ longSMS })
        .then(() => {
          setError("");
        })
        .catch((err) => setError(err.errors[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longSMS, unlimitedChecked]);

  // Actualizar estado cuando cambia el valor inicial
  useEffect(() => {
    if (initialLongSMSValue) {
      setLongSMS(initialLongSMSValue);
    }
  }, [initialLongSMSValue]);

  return (
    <div className={`${fullSize ? "" : "col-md-8"}`}>
      <div className="mt-3">
        <ToggleEnableActionWrapper
          title="UnlimitedLengthSMS"
          action="unlimitedsms"
          initialValue={initialUnlimitedValue}
          loadingListener
          fullSize
        />
      </div>
      {!unlimitedChecked && (
        <>
          <div className="d-flex col-md gap-2 justify-content-between align-items-center mx-4 my-3">
            <TitleConfig
              title={translate.SMSCharacterLimitAllowed}
              className="col-md-auto"
            />
            <div className="col-md-2">
              <NumberInput
                name="longSMS"
                min={MIN_LONG_SMS}
                className="form-control"
                defaultValue={longSMS}
                onChange={handleLongSMSChange}
                disabled={saving}
              />
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center my-2">
            <div
              id="errorMsj"
              className="mx-3 incorrect"
              style={{ color: "red", fontSize: "14px", marginTop: "10px" }}
            >
              {error}
            </div>
            {saveData && !error && (
              <ButtonPrimary onClick={saveValues} disabled={saving}>
                {translate.Save}
              </ButtonPrimary>
            )}
            {saving && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </div>
        </>
      )}
    </div>
  );
};
